import {
    required,
    validateData,
    onlyNumbers,
    length,
    minLength,
    maxLength,
    positive,
    email,
    invoiceNumbers,
    notEmpty,
    maxValue
} from '@utils/validators';
import language from '@language';
import { getMaxAmountCardInvoiceByCountry } from '@helpers/config-by-country';

export function validateDirectDebit(fields) {
    let validations = {
        document_type: [
            {
                fn: required,
                msg: true
            }
        ],
        document_number: [
            {
                fn: required,
                msg: true
            }
        ],
        account_type: [
            {
                fn: required,
                msg: true
            }
        ],
        bank_code: [
            {
                fn: required,
                msg: true
            }
        ],
        account_number: [
            {
                fn: required,
                msg: true
            }
        ],
        client_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: onlyNumbers,
                msg: language.form_validation.only_numbers
            },
            {
                fn: minLength(5),
                msg: language.form_validation.min_five_characters
            }
        ],
        property_name: [
            {
                fn: required,
                msg: true
            }
        ],
        holder_name: [
            {
                fn: required,
                msg: true
            }
        ],
        email: [
            {
                fn: required,
                msg: true
            },
            {
                fn: email,
                msg: true
            }
        ],
        cuit: [
            {
                fn: required,
                msg: true
            }
        ],
        invoice_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: invoiceNumbers,
                msg: language.form_validation.must_be_ten_digits
            }
        ],
        amount: [
            {
                fn: required,
                msg: true
            },
            {
                fn: positive,
                msg: language.form_validation.amount_higher_than_zero
            }
        ],
        cbu: [
            {
                fn: required,
                msg: true
            }
        ]
    };

    return validateData(validations, fields);
}

export function validateBankTransfer(fields, country) {
    let generals = {
        client_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: onlyNumbers,
                msg: language.form_validation.only_numbers
            },
            {
                fn: minLength(5),
                msg: language.form_validation.min_five_characters
            }
        ],
        property_name: [
            {
                fn: required,
                msg: true
            },
            {
                fn: maxLength(100),
                msg: language.form_validation.name_max_characters
            }
        ],
        holder_name: [
            {
                fn: required,
                msg: true
            },
            {
                fn: maxLength(100),
                msg: language.form_validation.name_max_characters
            }
        ],
        document: [
            {
                fn: required,
                msg: true
            }
        ],
        email: [
            {
                fn: required,
                msg: true
            },
            {
                fn: email,
                msg: true
            }
        ],
        invoice_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: invoiceNumbers,
                msg: language.form_validation.must_be_ten_digits
            }
        ],
        amount: [
            {
                fn: required,
                msg: true
            },
            {
                fn: positive,
                msg: language.form_validation.amount_higher_than_zero
            }
        ],
        bank_origin: [
            {
                fn: required,
                msg: true
            }
        ]
    };

    const byCountry = {
        AR: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: onlyNumbers,
                    msg: language.form_validation.only_numbers
                },
                {
                    fn: text => length(11)(text) || (minLength(7)(text) && maxLength(9)(text)),
                    msg: language.form_validation.format
                }
            ]
        },
        CO: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: onlyNumbers,
                    msg: language.form_validation.only_numbers
                },
                {
                    fn: minLength(6),
                    msg: language.form_validation.format
                },
                {
                    fn: maxLength(10),
                    msg: language.form_validation.format
                }
            ]
        },
        CL: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: minLength(8),
                    msg: language.form_validation.format
                },
                {
                    fn: maxLength(10),
                    msg: language.form_validation.format
                }
            ]
        }
            
    }

    const validations = { ...generals, ...byCountry[country] }

    return validateData(validations, fields);
}

export function validateBankTransferClaim(fields, country) {
    let generals = {
        client_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: onlyNumbers,
                msg: language.form_validation.only_numbers
            },
            {
                fn: minLength(5),
                msg: language.form_validation.min_five_characters
            }
        ],
        property_name: [
            {
                fn: required,
                msg: true
            },
            {
                fn: maxLength(100),
                msg: language.form_validation.property_name_max_characters
            }
        ],
        holder_name: [
            {
                fn: required,
                msg: true
            },
            {
                fn: maxLength(100),
                msg: language.form_validation.holder_name_max_characters
            }
        ],
        document: [
            {
                fn: required,
                msg: true
            }
        ],
        email: [
            {
                fn: required,
                msg: true
            },
            {
                fn: email,
                msg: true
            }
        ],
        invoice_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: invoiceNumbers,
                msg: language.form_validation.must_be_ten_digits
            }
        ],
        amount: [
            {
                fn: required,
                msg: true
            },
            {
                fn: positive,
                msg: language.form_validation.amount_higher_than_zero
            }
        ],
        bank_origin: [
            {
                fn: required,
                msg: true
            }
        ],
        voucher: [
            {
                fn: notEmpty,
                msg: true
            }
        ]
    };

    const byCountry = {
        AR: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: onlyNumbers,
                    msg: language.form_validation.only_numbers
                },
                {
                    fn: text => length(11)(text) || (minLength(7)(text) && maxLength(9)(text)),
                    msg: language.form_validation.format
                }
            ]
        },
        CO: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: onlyNumbers,
                    msg: language.form_validation.only_numbers
                },
                {
                    fn: minLength(6),
                    msg: language.form_validation.format
                },
                {
                    fn: maxLength(10),
                    msg: language.form_validation.format
                }
            ]
        },
        CL: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: onlyNumbers,
                    msg: language.form_validation.only_numbers
                },
                {
                    fn: minLength(8),
                    msg: language.form_validation.format
                },
                {
                    fn: maxLength(9),
                    msg: language.form_validation.format
                }
            ]
        }
            
    }

    const validations = { ...generals, ...byCountry[country] }
    return validateData(validations, fields);
}

export function validateFormCard(fields, country) {

    const byCountry = {
        CO: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: minLength(8),
                    msg: language.card.document_min_characters
                },
                {
                    fn: maxLength(15),
                    msg: language.card.document_max_characters
                }
            ]
        },
        CL: {
            document: [
                {
                    fn: required,
                    msg: true
                },
                {
                    fn: minLength(8),
                    msg: language.card.document_min_characters
                },
                {
                    fn: maxLength(11),
                    msg: language.card.document_max_characters
                }
            ]
        }
            
    }

    const generals = {
        client_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: onlyNumbers,
                msg: language.form_validation.only_numbers
            },
            {
                fn: minLength(5),
                msg: language.form_validation.length_booking_client
            },
            {
                fn: maxLength(8),
                msg: language.form_validation.length_booking_client
            }
        ],
        property_name: [
            {
                fn: required,
                msg: true
            },
            {
                fn: minLength(1),
                msg: language.form_validation.min_five_characters
            },
            {
                fn: maxLength(50),
                msg: language.form_validation.name_max_characters
            }
        ],
        email: [
            {
                fn: required,
                msg: true
            },
            {
                fn: email,
                msg: language.form_validation.email
            }
        ]
    };

    const validations = { ...generals, ...byCountry[country] }

    return { ...validateData(validations, fields), ...validateCardInvoice(fields, country) };
}

const card_invoice_validation = (maxAmount) => {
    return {
        invoice_number: [
            {
                fn: required,
                msg: true
            },
            {
                fn: invoiceNumbers,
                msg: language.form_validation.must_be_ten_digits
            }
        ],
        amount: [
            {
                fn: maxValue(maxAmount),
                msg: language.form_validation.amount_exceeded + maxAmount
            }
        ]
    }
};

function validateCardInvoice(fields, country) {
    const maxAmount = getMaxAmountCardInvoiceByCountry(country);

    let invoiceValidation = [];
    fields.invoices.forEach(invoice => {
        invoiceValidation = [ ...invoiceValidation, { ...validateData(card_invoice_validation(maxAmount), invoice) }];
    });
    const hasError = invoiceValidation.find(iv => (!!iv.invoice_number && iv.invoice_number.length > 0 || 
                                                   !!iv.amount && iv.amount.length > 0));
    return (!!hasError) ? { "invoices": invoiceValidation } : null;
}